/**
 * Inline Suggestions Block Renderer (formerly Pills)
 * Renders inline CTA "suggestion pills" using @strivelabs/ui-blocks Suggestions components
 */

import React from "react";
import type { UIBlock, UIActionEvent } from "@/types/ui-blocks";
import { Suggestions as UISuggestions, contractButtonStyle } from "@strivelabs/ui-blocks";
import { Button } from "@strivelabs/ui-core/v2";

export interface InlineSuggestionsBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled */
  readOnly?: boolean;
}

export function InlineSuggestionsBlockRenderer({
  block,
  onUIAction,
  readOnly = false
}: InlineSuggestionsBlockRendererProps) {
  if (block.type !== "inline_suggestions") return null;

  // Don't render inline suggestions in read-only mode
  if (readOnly) return null;

  const { content } = block;

  const handleClick = (item: { label: string; value: string; shouldRedirect?: boolean }) => {
    onUIAction({
      type: "ui_action",
      component: "inline_suggestions",
      label: content.label,
      state: { 
        action: { value: item.value, label: item.label },
        shouldRedirect: item.shouldRedirect ?? false
      },
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {content.label ? <div className="text-sm font-medium text-muted-foreground">{content.label}</div> : null}
      <UISuggestions className="justify-start">
        {content.items.map((item, idx) => {
          return (
            <Button
              key={`${item.value}-${idx}`}
              onClick={() => handleClick(item)}
              {...contractButtonStyle({
                variant: item.variant ?? "outline",
                size: item.size ?? "sm",
                colorVariant: item.colorVariant,
              })}
              disabled={item.disabled}
            >
              {item.label}
            </Button>
          );
        })}
      </UISuggestions>
    </div>
  );
}

/**
 * Backward compatibility: alias for old pills naming
 * @deprecated Use InlineSuggestionsBlockRenderer instead
 */
export const PillsBlockRenderer = InlineSuggestionsBlockRenderer;
